import * as React from 'react';
import ReactDatePicker from 'react-datepicker';
import { Calendar, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

import 'react-datepicker/dist/react-datepicker.css';

/**
 * DatePicker - A date picker component using shadcn/radix-ui patterns
 *
 * @param {Object} props
 * @param {Date|string} props.value - Selected date (Date object or YYYY-MM-DD string)
 * @param {Function} props.onChange - Callback when date changes (receives YYYY-MM-DD string)
 * @param {string} props.placeholder - Placeholder text
 * @param {Date|string} props.minDate - Minimum selectable date
 * @param {Date|string} props.maxDate - Maximum selectable date
 * @param {boolean} props.disabled - Disable the component
 * @param {string} props.className - Additional classes for the trigger button
 */
export function DatePicker({
	value,
	onChange,
	placeholder = 'Select date',
	minDate,
	maxDate,
	disabled = false,
	className,
}) {
	const [open, setOpen] = React.useState(false);

	// Convert string to Date if needed
	const dateValue = React.useMemo(() => {
		if (!value) return null;
		if (value instanceof Date) return value;
		// Parse YYYY-MM-DD string
		const [year, month, day] = value.split('-').map(Number);
		return new Date(year, month - 1, day);
	}, [value]);

	const minDateValue = React.useMemo(() => {
		if (!minDate) return null;
		if (minDate instanceof Date) return minDate;
		const [year, month, day] = minDate.split('-').map(Number);
		return new Date(year, month - 1, day);
	}, [minDate]);

	const maxDateValue = React.useMemo(() => {
		if (!maxDate) return null;
		if (maxDate instanceof Date) return maxDate;
		const [year, month, day] = maxDate.split('-').map(Number);
		return new Date(year, month - 1, day);
	}, [maxDate]);

	const handleSelect = (date) => {
		if (date) {
			// Format as YYYY-MM-DD
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			onChange(`${year}-${month}-${day}`);
		} else {
			onChange(null);
		}
		setOpen(false);
	};

	const displayLabel = dateValue
		? format(dateValue, 'EEE, MMM d, yyyy')
		: placeholder;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild disabled={disabled}>
				<button
					type="button"
					role="combobox"
					aria-expanded={open}
					className={cn(
						'flex h-[42px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
						!value && 'text-gray-400',
						className
					)}
					disabled={disabled}
				>
					<div className="flex items-center gap-2">
						<Calendar className="h-4 w-4 text-gray-400" />
						<span className={cn(!value && 'text-gray-400', value && 'text-gray-900')}>
							{displayLabel}
						</span>
					</div>
					<ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
				</button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<ReactDatePicker
					selected={dateValue}
					onChange={handleSelect}
					minDate={minDateValue}
					maxDate={maxDateValue}
					inline
					calendarClassName="!border-0 !rounded-md !shadow-none"
				/>
			</PopoverContent>
		</Popover>
	);
}

/**
 * DateRangePicker - A combined start/end date picker on one line
 *
 * @param {Object} props
 * @param {string} props.startDate - Start date (YYYY-MM-DD string)
 * @param {string} props.endDate - End date (YYYY-MM-DD string)
 * @param {Function} props.onStartDateChange - Callback when start date changes
 * @param {Function} props.onEndDateChange - Callback when end date changes
 * @param {Date|string} props.minDate - Minimum selectable date
 * @param {boolean} props.disabled - Disable the component
 */
export function DateRangePicker({
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	minDate,
	disabled = false,
	className,
}) {
	return (
		<div className={cn('flex items-center gap-2', className)}>
			<div className="flex-1">
				<DatePicker
					value={startDate}
					onChange={onStartDateChange}
					placeholder="Start date"
					minDate={minDate}
					disabled={disabled}
				/>
			</div>
			<span className="text-gray-400 text-sm font-medium">to</span>
			<div className="flex-1">
				<DatePicker
					value={endDate}
					onChange={onEndDateChange}
					placeholder="End date"
					minDate={startDate || minDate}
					disabled={disabled}
				/>
			</div>
		</div>
	);
}

export default DatePicker;
